import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Sparkles, 
  Car, 
  Phone, 
  Mail, 
  MapPin, 
  Star, 
  Clock, 
  Shield, 
  Droplets,
  ChevronRight,
  Menu,
  X,
  QrCode
} from "lucide-react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (error) {
        setLoadError('Unable to load services');
      }
    };

    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        setReviews(res.data);
      } catch (error) {
        // Reviews are non-critical, use defaults
      }
    };

    fetchServices();
    fetchReviews();
  }, []);

  const bookingUrl = typeof window !== 'undefined' ? `${window.location.origin}/book` : '/book';

  return (
    <div className="min-h-screen bg-background">
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-sky-600 to-sky-500 text-white py-3 px-4 text-center relative z-50" data-testid="promo-banner">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <span className="text-sm sm:text-base font-medium">
            ☀️ Summer Special: <strong className="text-yellow-300">20% OFF</strong> all services!
          </span>
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold tracking-wider">
            Code: SUMMER26
          </span>
          <Link to="/book" className="text-yellow-300 hover:text-yellow-200 text-sm font-semibold underline underline-offset-2">
            Book Now →
          </Link>
        </div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-12 left-0 right-0 z-50 nav-glass border-b border-white/10" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-sky-400" />
              <span className="logo-text text-xl text-white">RISE & SHINE</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-gray-300 hover:text-white transition-colors">Services</a>
              <a href="#reviews" className="text-gray-300 hover:text-white transition-colors">Reviews</a>
              <a href="#contact" className="text-gray-300 hover:text-white transition-colors">Contact</a>
              <Link to="/book">
                <Button data-testid="nav-book-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-white/10">
            <div className="px-4 py-4 space-y-3">
              <a href="#services" className="block text-gray-300 hover:text-white py-2">Services</a>
              <a href="#reviews" className="block text-gray-300 hover:text-white py-2">Reviews</a>
              <a href="#contact" className="block text-gray-300 hover:text-white py-2">Contact</a>
              <Link to="/book" className="block">
                <Button className="w-full bg-sky-500 hover:bg-sky-600">Book Now</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-bg pt-12" data-testid="hero-section">
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight mb-6">
              We Bring The <span className="text-sky-400">Shine</span> To You
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              Professional mobile auto detailing right in your driveway. 
              No hassle, no waiting rooms. Just a sparkling clean car.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book">
                <Button 
                  data-testid="hero-book-btn"
                  size="lg" 
                  className="bg-sky-500 hover:bg-sky-600 text-white text-lg px-8 py-6 btn-shine"
                >
                  Book Appointment
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:9058691069">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  905 869-1069
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4 p-6">
              <div className="p-3 bg-sky-500/10 rounded-lg">
                <Clock className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Convenient Scheduling</h3>
                <p className="text-gray-400">Book online 24/7. We come to you Thursday evenings through Sunday.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6">
              <div className="p-3 bg-sky-500/10 rounded-lg">
                <Shield className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Quality Guaranteed</h3>
                <p className="text-gray-400">Premium products and meticulous attention to detail every time.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6">
              <div className="p-3 bg-sky-500/10 rounded-lg">
                <Droplets className="w-6 h-6 text-sky-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Eco-Friendly</h3>
                <p className="text-gray-400">Water-conscious methods and environmentally safe cleaning products.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-background" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground uppercase tracking-tight mb-4">
              Our Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the perfect package for your vehicle. All services performed at your location.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className={`service-card relative overflow-hidden bg-card border-border/50 ${
                  service.id === 'deal' ? 'ring-2 ring-sky-500' : ''
                }`}
                data-testid={`service-card-${service.id}`}
              >
                {service.id === 'deal' && (
                  <div className="absolute top-4 right-4 z-10 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full best-value-badge">
                    BEST VALUE
                  </div>
                )}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.name}
                    className="w-full h-full object-cover service-image"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">{service.name}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{service.description}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-3xl font-bold text-sky-500">${service.price}</span>
                      <span className="text-muted-foreground text-sm ml-2">
                        / {service.duration_hours}hr{service.duration_hours > 1 ? 's' : ''}
                      </span>
                    </div>
                    <Link to={`/book?service=${service.id}`}>
                      <Button size="sm" className="bg-sky-500 hover:bg-sky-600">
                        Book
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-20 bg-slate-900 overflow-hidden" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight mb-4">
              What Our Clients Say
            </h2>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={`header-star-${i}`} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-gray-400">5.0 average from our satisfied customers</p>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative">
          <div className="flex animate-marquee">
            {[...reviews, ...reviews].map((review, idx) => (
              <div 
                key={`${review.id}-${idx}`} 
                className="flex-shrink-0 w-80 mx-4 testimonial-card rounded-xl p-6"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={`star-${review.id}-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">&ldquo;{review.comment}&rdquo;</p>
                <p className="text-white font-semibold">{review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 bg-slate-900 rounded-2xl p-8 lg:p-12">
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4">
                Scan to Book
              </h2>
              <p className="text-gray-400 mb-6 max-w-md">
                Share with friends or save for later. Scan this QR code to instantly access our booking page.
              </p>
              <Button 
                onClick={() => setShowQR(!showQR)}
                variant="outline"
                className="border-sky-500 text-sky-400 hover:bg-sky-500/10"
                data-testid="show-qr-btn"
              >
                <QrCode className="mr-2 w-5 h-5" />
                {showQR ? 'Hide' : 'Show'} QR Code
              </Button>
            </div>
            
            {showQR && (
              <div className="qr-container p-6 rounded-xl" data-testid="qr-code-container">
                <QRCodeSVG 
                  value={bookingUrl}
                  size={200}
                  bgColor="transparent"
                  fgColor="#38bdf8"
                  level="H"
                  includeMargin={true}
                />
                <p className="text-center text-sky-400 mt-4 text-sm font-medium">
                  Rise & Shine Auto Detailing
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-slate-900" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight mb-6">
                Get In Touch
              </h2>
              <p className="text-gray-400 mb-8 text-lg">
                Ready to make your car shine? Book online or give us a call. 
                We serve the greater area with pride.
              </p>
              <div className="space-y-6">
                <a href="tel:9058691069" className="flex items-center gap-4 text-gray-300 hover:text-white transition-colors">
                  <div className="p-3 bg-sky-500/10 rounded-lg">
                    <Phone className="w-6 h-6 text-sky-400" />
                  </div>
                  <span className="text-lg">905 869-1069</span>
                </a>
                <a href="mailto:jahh88@icloud.com" className="flex items-center gap-4 text-gray-300 hover:text-white transition-colors">
                  <div className="p-3 bg-sky-500/10 rounded-lg">
                    <Mail className="w-6 h-6 text-sky-400" />
                  </div>
                  <span className="text-lg">jahh88@icloud.com</span>
                </a>
                <div className="flex items-center gap-4 text-gray-300">
                  <div className="p-3 bg-sky-500/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-sky-400" />
                  </div>
                  <span className="text-lg">Mobile Service - We Come to You!</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Operating Hours</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>Monday - Friday</span>
                  <span className="text-sky-400">12:00 PM, 5:00 PM</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Saturday</span>
                  <span className="text-sky-400">9:00 AM, 2:00 PM</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Sunday</span>
                  <span className="text-sky-400">9:00 AM, 2:00 PM</span>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/book">
                  <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white py-6 text-lg">
                    Book Your Appointment
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-950 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-sky-400" />
              <span className="logo-text text-lg text-white">RISE & SHINE AUTO DETAILING</span>
            </div>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Rise & Shine Auto Detailing. All rights reserved.
            </p>
            <Link to="/admin" className="text-gray-500 hover:text-gray-400 text-sm">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
